import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { CircleRingIcon, SkypeCircleCheckIcon } from 'components/Checklist/CheckListStyles';
import { globalStackTokensGapExtraSmall, globalStackTokensGapMedium, userIcon } from 'globalStyles';
import Tag from 'models/tag';
import Task from 'models/tasks/task';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateShort, toLocaleDateTimeMedium, toLocaleTimeNoSeconds } from 'utils/datetime';
import { htmlToText } from 'utils/html';
import { TaskDetailAssignment } from './TaskDetailAssignment';
import { TaskDetailDeadline } from './TaskDetailDeadline';
import { TaskDetailDescription } from './TaskDetailDecription';
import { TaskDetailExternalId } from './TaskDetailExternalId';
import { TaskDetailOwner } from './TaskDetailOwner';
import { TaskDetailRecurrenceModal } from './TaskDetailRecurrence';
import {
  canShowTaskExternalId,
  canShowTaskRecurrence,
  canShowTaskSchema,
  getTaskDeadlineLabel,
  getTaskForRecurrenceDialog,
  isLegacyNormalSeries,
  getTaskSchemaName,
} from './taskDetailRules';
import { TaskDetailRow } from './TaskDetailRow';
import { TaskDetailStart } from './TaskDetailStart';
import { TaskDetailStatus } from './TaskDetailStatus';
import { TaskDetailTags } from './TaskDetailTags';

export interface ITaskDetailPanelProps {
  task: Task;
  orgTask: Task;
  getErrorMessage: (field: string) => string | undefined;
  onUpdate: (newTask: Task) => void;
  isActionPending: boolean;
  loadRescheduleRange: () => void;
  tags: Tag[];
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
  canUpdate: boolean;
  //an event series has no assignee and no deadline of its own, and its start has no end time
  isEventSeries?: boolean;
}

// The details as a list of To Do style rows: icon + "Label: value", click opens the existing editor
// in a callout
export const TaskDetailPanel = (props: ITaskDetailPanelProps) => {
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const [recurrenceModalOpen, setRecurrenceModalOpen] = useState<boolean>(false);
  const { t } = useTranslation(['task', 'tags', 'translation', 'adminAuth']);
  const task = props.task;
  const none = t('task:Detail.None');
  const isReadonly = (field: TaskFieldTypes): boolean =>
    !canUpdateTaskField(props.orgTask, props.canUpdate, field, appContext);

  //a field with a validation error shows its row in the error state: that is why Save is disabled
  const hasError = (field: string): boolean => !!props.getErrorMessage(field);

  const getDescriptionText = (): string => {
    const firstLine = htmlToText(task.description, false)
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.length > 0);

    return firstLine || none;
  };

  const getStatusText = (): string => {
    let text = appContext.globalDataCache.taskStates.get(task.taskStateId).state;
    if (task.isCompleted()) {
      text += `, ${t('task:Completed.Label').toLowerCase()} ${toLocaleDateTimeMedium(task.completed)}`;
    }

    return text;
  };

  // A recurring normal task has a computed deadline, TaskDetailDeadline handles that case
  const hasComputedDeadline = task.hasComputedDeadline();
  const deadline = hasComputedDeadline ? task.getDeadline(appContext.globalDataCache) : task.deadline;
  const isOverdue = task.isOverDeadline(deadline, new Date()) && !task.isCompleted();

  const showSchema = props.isEventSeries || canShowTaskSchema(appContext);
  const showRecurrence = canShowTaskRecurrence(props.orgTask);
  const showExternalId = canShowTaskExternalId(appContext);

  // the row opens the recurrence dialog at once, which starts from the planned start date
  const openRecurrenceModal = () => {
    props.onUpdate(getTaskForRecurrenceDialog(task));
    setRecurrenceModalOpen(true);
  };

  //
  // Main render
  //
  return (
    <>
      <IPStack tokens={globalStackTokensGapMedium}>
        <IPStack tokens={globalStackTokensGapExtraSmall}>
          <TaskDetailRow
            iconName="Tag"
            label={t('tags:General.Name')}
            value={props.tags.map((tag) => tag.value()).join(', ') || none}
            disabled={isReadonly(TaskFieldTypes.Tags)}
          >
            <IPStack>
              <IPLabel>{t('tags:General.Name')}</IPLabel>
              <TaskDetailTags
                orgTask={props.orgTask}
                task={task}
                tags={props.tags}
                canUpdate={props.canUpdate}
                addTagToTaskState={props.addTagToTaskState}
                removeTagFromTaskState={props.removeTagFromTaskState}
                isActionPending={props.isActionPending}
              />
            </IPStack>
          </TaskDetailRow>
          <TaskDetailRow
            iconName="TextDocument"
            label={t('task:Description.Label')}
            value={getDescriptionText()}
            disabled={!task.description && isReadonly(TaskFieldTypes.Description)}
            calloutWidth={520}
          >
            <TaskDetailDescription
              orgTask={props.orgTask}
              task={task}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
            />
          </TaskDetailRow>
          {showSchema && (
            <TaskDetailRow
              iconName="People"
              label={t('adminAuth:AuthSchemaPicker.Label')}
              value={getTaskSchemaName(task, appContext, t)}
              disabled={isReadonly(TaskFieldTypes.Permissions)}
            >
              <TaskDetailAssignment
                orgTask={props.orgTask}
                task={task}
                canUpdate={props.canUpdate}
                onUpdate={props.onUpdate}
                getErrorMessage={props.getErrorMessage}
                hideAssignee
              />
            </TaskDetailRow>
          )}
          {!props.isEventSeries && (
            <TaskDetailRow
              iconName={userIcon.iconName ?? 'UserFollowed'}
              label={t('task:Assignment.Label')}
              value={task.userId ? appContext.globalDataCache.users.get(task.userId).name || none : none}
              //never disabled: the editor shows the read-only state and still offers "Assign to me"
              danger={hasError('assignment')}
            >
              <TaskDetailAssignment
                orgTask={props.orgTask}
                task={task}
                canUpdate={props.canUpdate}
                onUpdate={props.onUpdate}
                getErrorMessage={props.getErrorMessage}
                hidePermissions
              />
            </TaskDetailRow>
          )}
          <TaskDetailRow
            iconProps={task.isCompleted() ? SkypeCircleCheckIcon : CircleRingIcon}
            label={t('task:Status.Label')}
            value={getStatusText()}
            disabled={isReadonly(TaskFieldTypes.Status)}
            calloutWidth={task.isCompleted() ? 560 : 300}
          >
            <TaskDetailStatus
              task={task}
              orgTask={props.orgTask}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
              onChangeCompletionDate={props.onChangeCompletionDate}
              showCompleted={task.isCompleted()}
            />
          </TaskDetailRow>
          <TaskDetailRow
            iconName="Calendar"
            label={t('task:Planned.Label')}
            value={
              props.isEventSeries
                ? `${toLocaleDateShort(task.startDateTime)}, ${toLocaleTimeNoSeconds(task.startDateTime)}`
                : `${toLocaleDateShort(task.startDateTime)}, ${toLocaleTimeNoSeconds(
                    task.startDateTime,
                  )} - ${toLocaleTimeNoSeconds(task.endDateTime)}`
            }
            danger={hasError('startDateTime') || hasError('endDateTime')}
            disabled={isReadonly(TaskFieldTypes.Start)}
            calloutWidth={480}
          >
            <TaskDetailStart
              orgTask={props.orgTask}
              task={task}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
              getErrorMessage={props.getErrorMessage}
              loadRescheduleRange={props.loadRescheduleRange}
              hideEndTime={props.isEventSeries}
            />
          </TaskDetailRow>
          {!props.isEventSeries && (
            <TaskDetailRow
              iconName="AlarmClock"
              label={getTaskDeadlineLabel(task, t)}
              value={`${toLocaleDateShort(deadline)}, ${toLocaleTimeNoSeconds(deadline)}`}
              danger={isOverdue || hasError('deadline')}
              disabled={isReadonly(TaskFieldTypes.Deadline)}
              calloutWidth={hasComputedDeadline ? 560 : 480}
            >
              <TaskDetailDeadline
                orgTask={props.orgTask}
                task={task}
                canUpdate={props.canUpdate}
                onUpdate={props.onUpdate}
                getErrorMessage={props.getErrorMessage}
              />
            </TaskDetailRow>
          )}
          {showRecurrence && (
            <TaskDetailRow
              iconName="RepeatAll"
              label={t('task:Repeat.Title')}
              value={task.recurringPattern.isActive ? task.recurringPattern.getSummary() : none}
              disabled={isLegacyNormalSeries(props.orgTask) || isReadonly(TaskFieldTypes.Recurrence)}
              onActivate={openRecurrenceModal}
            />
          )}
          {showExternalId && (
            <TaskDetailRow
              iconName="Barcode"
              label={t('task:ExternalId.Label')}
              value={task.externalId || none}
              disabled={isReadonly(TaskFieldTypes.ExternalId)}
              calloutWidth={320}
            >
              <TaskDetailExternalId
                orgTask={props.orgTask}
                task={task}
                canUpdate={props.canUpdate}
                onUpdate={props.onUpdate}
              />
            </TaskDetailRow>
          )}
        </IPStack>
        <IPStack.Item styles={{ root: { paddingLeft: theme.spacing.s1 } }}>
          <TaskDetailOwner orgTask={props.orgTask} task={task} canUpdate={props.canUpdate} onUpdate={props.onUpdate} />
        </IPStack.Item>
      </IPStack>
      <TaskDetailRecurrenceModal
        task={task}
        onUpdate={props.onUpdate}
        isOpen={recurrenceModalOpen}
        toggleModal={() => setRecurrenceModalOpen(!recurrenceModalOpen)}
      />
    </>
  );
};
